// The Email insured step in the Record's words (spec C9, C16): the payment choices, what to fill
// in, the mailto link for Open in Outlook, and the "Sent" stamp. Pure functions for the step and
// the tests.
import type { EmailDraft, EmailPayment, FetchView } from '../auth/api';
import { stampTime } from './fetch-lines';

export const paymentChoices: { key: EmailPayment; label: string }[] = [
  { key: 'not_escrowed', label: 'Insured pays the carrier' },
  { key: 'escrowed', label: 'Bank escrows' },
  { key: 'due_to_safeside', label: 'Due to Safeside' },
];

/**
 * What the draft is built from on the Record: the files and the dec page's reading. When it changes
 * (a reading finishes, ID cards or a new dec page arrive, a value is corrected), the step asks for
 * the draft again.
 */
export function draftRevision(view: Pick<FetchView, 'documents' | 'reading'>): string {
  return [
    ...view.documents.map((d) => d.id),
    view.reading?.documentId ?? '',
    view.reading?.status ?? '',
    ...(view.reading?.fields ?? []).map((f) => f.fix?.at ?? ''),
  ].join('|');
}

export type EmailText = { to: string; cc: string; subject: string; body: string };

const sameText = (a: EmailText, b: EmailText) =>
  a.to === b.to && a.cc === b.cc && a.subject === b.subject && a.body === b.body;

/**
 * The email's text after a new draft arrives. A new template or payment block starts fresh, and so
 * does a newer draft while the VA has not changed the last one; the VA's own edits are kept.
 */
export function nextEmailText(
  current: EmailText,
  previous: EmailText | null,
  next: EmailText,
  choiceChanged: boolean,
): EmailText {
  const fresh = { to: next.to, cc: next.cc, subject: next.subject, body: next.body };
  if (choiceChanged || !previous || sameText(current, previous)) return fresh;
  return current;
}

/** "Fill in before sending: Payment link, CSR phone extension, and 3 [ ] in the text." */
export function fillInLine(missing: string[], fillIns: number): string | null {
  const parts: string[] = [];
  if (missing.length) parts.push(missing.join(', '));
  if (fillIns) parts.push(`${fillIns} [ ] in the text`);
  if (!parts.length) return null;
  return `Fill in before sending: ${parts.join(', and ')}.`;
}

/** Outlook links take commas between addresses. */
const addresses = (s: string) =>
  s
    .split(/[;,]/)
    .map((a) => a.trim())
    .filter(Boolean)
    .join(',');

/** The mailto link that opens a new message in the VA's own mail program, filled in. */
export function mailtoHref(e: { to: string; cc: string; subject: string; body: string }): string {
  const params = [`subject=${encodeURIComponent(e.subject)}`, `body=${encodeURIComponent(e.body)}`];
  const cc = addresses(e.cc);
  if (cc) params.unshift(`cc=${encodeURIComponent(cc)}`);
  return `mailto:${encodeURIComponent(addresses(e.to)).replace(/%2C/g, ',')}?${params.join('&')}`;
}

/**
 * A new message in Outlook on the web with To, CC, Subject and Body filled (works on Windows and
 * Mac in any browser; tested Oct 26). It cannot attach files: Download attachments and drag them in.
 */
export function outlookWebHref(e: { to: string; cc: string; subject: string; body: string }) {
  const params = [
    ['to', addresses(e.to)],
    ['cc', addresses(e.cc)],
    ['subject', e.subject],
    ['body', e.body],
  ]
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
  return `https://outlook.office.com/mail/deeplink/compose?${params.join('&')}`;
}

/** Some mail programs cut a mailto link this long; the step then points to Copy body. */
export const longMailto = 1900;

export const outlookFallback =
  'Outlook did not open? Copy the subject and body and paste them into a new email.';

export const longBodyLine =
  'This email is long: if Outlook cuts the text, use Copy body and paste it into the message.';

/** "Sent by Val Vee, Oct 26 14:20" */
export const sentStamp = (s: EmailDraft['sent'][number]) =>
  `Sent by ${s.by ?? 'someone'}, ${stampTime(s.at)}`;

export const templateNameOf = (d: EmailDraft, key: string) =>
  d.templates.find((t) => t.key === key)?.name ?? key;
